import { Form, GetRef, Input, Select } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styles from '../styles/styles.module.scss';

type FormInstance<T> = GetRef<typeof Form<T>>;

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface EditableRowProps {
  index: number;
}

export const EditableRow: React.FC<EditableRowProps> = ({
  index,
  ...props
}) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  showSearch: boolean;
  inputType?: string;
  editOptions?: { label?: string; value: string }[];
  dataIndex: string;
  record: Record<string, any>;
  handleSave: (record: Record<string, any>, updatedValues: Record<string, any>) => Promise<void>;
}

export const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title,
  editable,
  showSearch = false,
  inputType,
  editOptions,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<any>(null);
  const form = useContext(EditableContext)!;

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const getChanges = async () => {
    const values = await form.validateFields();
    const updatedValues: Record<string, any> = {};
    let updated = false;

    for (const key of Object.keys(values)) {
      const value = values[key];
      if (value !== record[key]) {
        updated = true;
        updatedValues[key] = value;
      }
    }
    return { updated, values: { ...record, ...values }, updatedValues };
  };

  const save = async () => {
    try {
      const { updated, values, updatedValues } = await getChanges();
      if (updated) {
        await handleSave(values, updatedValues);
      }
      toggleEdit();
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    const inputNode =
      inputType === 'select' ? (
        <Select
          ref={inputRef}
          suffixIcon={null}
          defaultOpen={true}
          {...(showSearch ? 
            {
              showSearch: true,
              allowClear: true,
              optionFilterProp: 'label',
              filterOption: (input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase()),
            }
            : {})}
          options={editOptions}
          onChange={save}
          onBlur={toggleEdit}
        />
      ) : (
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      );

    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        {inputNode}
      </Form.Item>
    ) : (
      <div
        className={styles.editable_cell_value_wrap}
        style={{ paddingInlineEnd: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};
